import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Alert } from 'react-bootstrap';
import { FaCalculator, FaPrint, FaMoneyBillWave, FaCalendarAlt, FaPercentage } from 'react-icons/fa';
import '../styles/creditCalculation.css';

const AdminCreditCalculationPage = () => {
    // Ana state'ler
    const [calculationType, setCalculationType] = useState('monthly');
    const [inputs, setInputs] = useState({
        principal: 0,
        rate: 0,
        term: 0,
        days: 0,
        deferredDays: 0,
        checkAmount: 0,
        checkDays: 0,
        discountRate: 0
    });
    const [results, setResults] = useState(null);
    const [installments, setInstallments] = useState([]);

    // Hesaplama türleri
    const calculationTypes = [
        { value: 'monthly', label: 'Aylık Faiz Hesaplama' },
        { value: 'daily', label: 'Günlük Faiz Hesaplama' },
        { value: 'check_discount', label: 'Çek Kırdırma (İskonto)' },
        { value: 'flexible_payment', label: 'Esnek Ödemeli (Ertelemeli)' }
    ];

    // PMT hesaplama (mevcut koddan)
    const calculatePMT = (rate, nper, pv) => {
        if (rate === 0) return pv / nper;
        const pvif = Math.pow(1 + rate, nper);
        return (rate * pv * pvif) / (pvif - 1);
    };

    // Günlük faiz hesaplama
    const calculateDailyInterest = () => {
        const { principal, rate, days } = inputs;
        const monthlyRate = rate / 100; // Aylık faiz oranı
        const dailyRate = monthlyRate / 30; // Günlük faiz oranı
        const dailyInterestAmount = principal * dailyRate; // Günlük faiz tutarı
        const totalInterest = dailyInterestAmount * days;
        const totalAmount = principal + totalInterest;

        return {
            principal: principal,
            monthlyRate: rate, // Aylık faiz oranı yüzde olarak
            days: days,
            dailyInterestAmount: dailyInterestAmount.toFixed(2),
            totalInterest: totalInterest.toFixed(2),
            totalAmount: totalAmount.toFixed(2)
        };
    };

    // Aylık faiz hesaplama
    const calculateMonthlyInterest = () => {
        const { principal, rate, term } = inputs;
        const monthlyRate = rate / 100;
        const monthlyPayment = calculatePMT(monthlyRate, term, principal);
        const totalPayment = monthlyPayment * term;
        const totalInterest = totalPayment - principal;

        // Taksit planı oluştur
        let remainingBalance = principal;
        const installmentPlan = [];

        for (let i = 1; i <= term; i++) {
            const interestPayment = remainingBalance * monthlyRate;
            const principalPayment = monthlyPayment - interestPayment;
            remainingBalance -= principalPayment;

            installmentPlan.push({
                month: i,
                payment: monthlyPayment.toFixed(2),
                principal: principalPayment.toFixed(2),
                interest: interestPayment.toFixed(2),
                balance: Math.max(0, remainingBalance).toFixed(2)
            });
        }

        setInstallments(installmentPlan);

        return {
            principal: principal,
            monthlyRate: rate,
            term: term,
            monthlyPayment: monthlyPayment.toFixed(2),
            totalPayment: totalPayment.toFixed(2),
            totalInterest: totalInterest.toFixed(2),
            annualRate: (monthlyRate * 12).toFixed(2)
        };
    };

    // Çek kırdırma hesaplama
    const calculateCheckDiscount = () => {
        const { checkAmount, checkDays, discountRate } = inputs;
        const annualDiscountRate = discountRate / 100;
        const discountAmount = checkAmount * annualDiscountRate * (checkDays / 365);
        const netAmount = checkAmount - discountAmount;
        const effectiveRate = (discountAmount / netAmount) * (365 / checkDays) * 100;

        return {
            checkAmount: checkAmount,
            checkDays: checkDays,
            discountRate: discountRate,
            discountAmount: discountAmount.toFixed(2),
            netAmount: netAmount.toFixed(2),
            effectiveAnnualRate: effectiveRate.toFixed(2)
        };
    };

    // Esnek ödemeli hesaplama
    const calculateFlexiblePayment = () => {
        const { principal, rate, term, deferredDays } = inputs;
        const monthlyRate = rate / 100;
        const dailyRate = monthlyRate / 30;

        // Erteleme faizi
        const deferredInterest = principal * dailyRate * deferredDays;
        const adjustedPrincipal = principal + deferredInterest;

        // Normal taksit hesaplama
        const monthlyPayment = calculatePMT(monthlyRate, term, adjustedPrincipal);
        const totalPayment = monthlyPayment * term;
        const totalInterest = totalPayment - principal;

        return {
            principal: principal,
            deferredDays: deferredDays,
            deferredInterest: deferredInterest.toFixed(2),
            adjustedPrincipal: adjustedPrincipal.toFixed(2),
            monthlyRate: rate,
            term: term,
            monthlyPayment: monthlyPayment.toFixed(2),
            totalPayment: totalPayment.toFixed(2),
            totalInterest: totalInterest.toFixed(2)
        };
    };

    // Ana hesaplama fonksiyonu
    const handleCalculate = () => {
        let calculationResult;

        switch (calculationType) {
            case 'monthly':
                calculationResult = calculateMonthlyInterest();
                break;
            case 'daily':
                calculationResult = calculateDailyInterest();
                break;
            case 'check_discount':
                calculationResult = calculateCheckDiscount();
                break;
            case 'flexible_payment':
                calculationResult = calculateFlexiblePayment();
                break;
            default:
                return;
        }

        setResults(calculationResult);
    };

    // Input değişiklik handler
    const handleInputChange = (field, value) => {
        setInputs(prev => ({
            ...prev,
            [field]: parseFloat(value) || 0
        }));
    };

    // Hesaplama türü değişiklik handler
    const handleTypeChange = (type) => {
        setCalculationType(type);
        setResults(null);
        setInstallments([]);
    };

    // Print fonksiyonu
    const handlePrint = () => {
        window.print();
    };

    return (
        <Container className="credit-calculation-page py-4">
            <Row className="mb-4">
                <Col>
                    <h1 className="text-center mb-3">
                        <FaCalculator className="me-2" />
                        Kredi Hesaplama Merkezi
                    </h1>
                    <p className="text-center text-muted">
                        Profesyonel finansal hesaplama araçları - Sadece hesaplama, kayıt yapılmaz
                    </p>
                </Col>
            </Row>

            {/* Hesaplama Türü Seçimi */}
            <Row className="mb-4">
                <Col md={8} className="mx-auto">
                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">Hesaplama Türü Seçin</h5>
                        </Card.Header>
                        <Card.Body>
                            <Form.Group>
                                <Form.Select
                                    value={calculationType}
                                    onChange={(e) => handleTypeChange(e.target.value)}
                                    size="lg"
                                >
                                    {calculationTypes.map(type => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                {/* Sol Kolon - Input Form */}
                <Col md={4}>
                    <Card className="mb-4">
                        <Card.Header>
                            <h6 className="mb-0">
                                <FaMoneyBillWave className="me-2" />
                                Hesaplama Parametreleri
                            </h6>
                        </Card.Header>
                        <Card.Body>
                            {/* Günlük Faiz Input'ları */}
                            {calculationType === 'daily' && (
                                <>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Ana Para (TL)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={inputs.principal}
                                            onChange={(e) => handleInputChange('principal', e.target.value)}
                                            placeholder="Örn: 100000"
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Aylık Faiz Oranı (%)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            value={inputs.rate}
                                            onChange={(e) => handleInputChange('rate', e.target.value)}
                                            placeholder="Örn: 3"
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Gün Sayısı</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={inputs.days}
                                            onChange={(e) => handleInputChange('days', e.target.value)}
                                            placeholder="Örn: 30"
                                        />
                                    </Form.Group>
                                </>
                            )}

                            {/* Aylık Faiz Input'ları */}
                            {calculationType === 'monthly' && (
                                <>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Ana Para (TL)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={inputs.principal}
                                            onChange={(e) => handleInputChange('principal', e.target.value)}
                                            placeholder="Örn: 100000"
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Aylık Faiz Oranı (%)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            value={inputs.rate}
                                            onChange={(e) => handleInputChange('rate', e.target.value)}
                                            placeholder="Örn: 3.5"
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Vade (Ay)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={inputs.term}
                                            onChange={(e) => handleInputChange('term', e.target.value)}
                                            placeholder="Örn: 12"
                                        />
                                    </Form.Group>
                                </>
                            )}

                            {/* Çek Kırdırma Input'ları */}
                            {calculationType === 'check_discount' && (
                                <>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Çek Tutarı (TL)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={inputs.checkAmount}
                                            onChange={(e) => handleInputChange('checkAmount', e.target.value)}
                                            placeholder="Örn: 50000"
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Vade Süresi (Gün)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={inputs.checkDays}
                                            onChange={(e) => handleInputChange('checkDays', e.target.value)}
                                            placeholder="Örn: 90"
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>İskonto Oranı (% Yıllık)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            value={inputs.discountRate}
                                            onChange={(e) => handleInputChange('discountRate', e.target.value)}
                                            placeholder="Örn: 25"
                                        />
                                    </Form.Group>
                                </>
                            )}

                            {/* Esnek Ödeme Input'ları */}
                            {calculationType === 'flexible_payment' && (
                                <>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Ana Para (TL)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={inputs.principal}
                                            onChange={(e) => handleInputChange('principal', e.target.value)}
                                            placeholder="Örn: 100000"
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Aylık Faiz Oranı (%)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            value={inputs.rate}
                                            onChange={(e) => handleInputChange('rate', e.target.value)}
                                            placeholder="Örn: 3.5"
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Vade (Ay)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={inputs.term}
                                            onChange={(e) => handleInputChange('term', e.target.value)}
                                            placeholder="Örn: 12"
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Erteleme Süresi (Gün) - Max 90</Form.Label>
                                        <Form.Control
                                            type="number"
                                            max="90"
                                            value={inputs.deferredDays}
                                            onChange={(e) => handleInputChange('deferredDays', Math.min(90, e.target.value))}
                                            placeholder="Örn: 30"
                                        />
                                    </Form.Group>
                                </>
                            )}

                            <Button
                                variant="primary"
                                onClick={handleCalculate}
                                className="w-100 mt-3"
                                size="lg"
                            >
                                <FaCalculator className="me-2" />
                                Hesapla
                            </Button>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Sağ Kolon - Sonuçlar */}
                <Col md={8}>
                    {results && (
                        <Card className="mb-4">
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <h6 className="mb-0">
                                    <FaPercentage className="me-2" />
                                    Hesaplama Sonuçları
                                </h6>
                                <Button variant="outline-secondary" onClick={handlePrint}>
                                    <FaPrint className="me-2" />
                                    Yazdır
                                </Button>
                            </Card.Header>
                            <Card.Body>
                                {/* Günlük Faiz Sonuçları */}
                                {calculationType === 'daily' && results && (
                                    <Row>
                                        <Col md={6}>
                                            <p><strong>Ana Para:</strong> ₺{parseInt(results.principal).toLocaleString('tr-TR')}</p>
                                            <p><strong>Aylık Faiz Oranı:</strong> %{results.monthlyRate}</p>
                                            <p><strong>Gün Sayısı:</strong> {results.days} gün</p>
                                        </Col>
                                        <Col md={6}>
                                            <p><strong>Günlük Faiz Tutarı:</strong> ₺{parseFloat(results.dailyInterestAmount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</p>
                                            <p><strong>Toplam Faiz:</strong> ₺{parseFloat(results.totalInterest).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</p>
                                            <p><strong>Toplam Geri Ödeme:</strong> ₺{parseFloat(results.totalAmount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</p>
                                        </Col>
                                    </Row>
                                )}

                                {/* Aylık Faiz Sonuçları */}
                                {calculationType === 'monthly' && results && (
                                    <>
                                        <Row className="mb-3">
                                            <Col md={6}>
                                                <p><strong>Ana Para:</strong> ₺{parseInt(results.principal).toLocaleString('tr-TR')}</p>
                                                <p><strong>Aylık Faiz Oranı:</strong> %{results.monthlyRate}</p>
                                                <p><strong>Vade:</strong> {results.term} ay</p>
                                            </Col>
                                            <Col md={6}>
                                                <p><strong>Aylık Ödeme:</strong> ₺{parseFloat(results.monthlyPayment).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</p>
                                                <p><strong>Toplam Ödeme:</strong> ₺{parseFloat(results.totalPayment).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</p>
                                                <p><strong>Toplam Faiz:</strong> ₺{parseFloat(results.totalInterest).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</p>
                                            </Col>
                                        </Row>

                                        {/* Taksit Planı */}
                                        {installments.length > 0 && (
                                            <div>
                                                <h6 className="mt-3 mb-3">Aylık Taksit Planı:</h6>
                                                <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                                    <Table striped bordered hover size="sm">
                                                        <thead>
                                                            <tr>
                                                                <th>Ay</th>
                                                                <th>Taksit Tutarı</th>
                                                                <th>Ana Para</th>
                                                                <th>Faiz</th>
                                                                <th>Kalan Bakiye</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {installments.map(installment => (
                                                                <tr key={installment.month}>
                                                                    <td>{installment.month}</td>
                                                                    <td>₺{parseFloat(installment.payment).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
                                                                    <td>₺{parseFloat(installment.principal).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
                                                                    <td>₺{parseFloat(installment.interest).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
                                                                    <td>₺{parseFloat(installment.balance).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}

                                {/* Çek Kırdırma Sonuçları */}
                                {calculationType === 'check_discount' && results && (
                                    <Row>
                                        <Col md={6}>
                                            <p><strong>Çek Tutarı:</strong> ₺{parseInt(results.checkAmount).toLocaleString('tr-TR')}</p>
                                            <p><strong>Vade:</strong> {results.checkDays} gün</p>
                                            <p><strong>İskonto Oranı:</strong> %{results.discountRate} (Yıllık)</p>
                                        </Col>
                                        <Col md={6}>
                                            <p><strong>İskonto Tutarı:</strong> ₺{parseFloat(results.discountAmount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</p>
                                            <p><strong>Net Alınacak Tutar:</strong> ₺{parseFloat(results.netAmount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</p>
                                            <p><strong>Etkili Yıllık Maliyet:</strong> %{results.effectiveAnnualRate}</p>
                                        </Col>
                                    </Row>
                                )}

                                {/* Esnek Ödeme Sonuçları */}
                                {calculationType === 'flexible_payment' && results && (
                                    <Row>
                                        <Col md={6}>
                                            <p><strong>Ana Para:</strong> ₺{parseInt(results.principal).toLocaleString('tr-TR')}</p>
                                            <p><strong>Erteleme Süresi:</strong> {results.deferredDays} gün</p>
                                            <p><strong>Erteleme Faizi:</strong> ₺{parseFloat(results.deferredInterest).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</p>
                                            <p><strong>Düzeltilmiş Ana Para:</strong> ₺{parseFloat(results.adjustedPrincipal).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</p>
                                        </Col>
                                        <Col md={6}>
                                            <p><strong>Vade:</strong> {results.term} ay</p>
                                            <p><strong>Aylık Ödeme:</strong> ₺{parseFloat(results.monthlyPayment).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</p>
                                            <p><strong>Toplam Ödeme:</strong> ₺{parseFloat(results.totalPayment).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</p>
                                            <p><strong>Toplam Faiz:</strong> ₺{parseFloat(results.totalInterest).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</p>
                                        </Col>
                                    </Row>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {/* Bilgilendirme */}
                    {!results && (
                        <Alert variant="info">
                            <h6>Kredi Hesaplama Merkezi</h6>
                            <p className="mb-0">
                                Sol taraftan hesaplama türünü seçin, parametreleri girin ve "Hesapla" butonuna tıklayın.
                                Sonuçlar bu alanda görüntülenecek ve yazdırabileceksiniz.
                            </p>
                        </Alert>
                    )}
                </Col>
            </Row>
        </Container>
    );
};

export default AdminCreditCalculationPage;